/**
 * Backend timestamps come back in two shapes:
 *   1. ISO string (typed in api.ts), e.g. "2026-04-25T15:00:00Z"
 *   2. Firestore Timestamp object, e.g. { _seconds: 1777109767, _nanoseconds: 267000000 }
 *
 * The OpenAPI spec types these as strings, but Cloud Functions handlers that
 * res.json() a Firestore document leak the raw Timestamp shape. The legacy
 * SSE event types in useLegacyUpdatesStream.ts already model the second shape;
 * the new wire-up surface should accept both at the rendering boundary so a
 * single backend serialization gap doesn't show up as "NaNd ago" in the UI.
 */
export type ApiTimestamp =
  | string
  | { _seconds: number; _nanoseconds?: number }
  | undefined
  | null

/** Parse either backend timestamp shape into a Date (null when unusable). */
export function toDate(value: ApiTimestamp): Date | null {
  if (!value) return null
  if (typeof value === 'string') {
    const d = new Date(value)
    return isNaN(d.getTime()) ? null : d
  }
  if (typeof value === 'object' && typeof value._seconds === 'number') {
    return new Date(value._seconds * 1000)
  }
  return null
}

/** Format any backend timestamp shape into a relative label like "3d ago". */
export function formatRelativeDate(value: ApiTimestamp): string {
  const date = toDate(value)
  if (!date) return ''
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / 86_400_000)
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/**
 * Like formatRelativeDate, but with finer granularity (minutes/hours) for the
 * "uploaded N min ago" line under upload sources. Returns 'just now' when the
 * timestamp is missing entirely.
 */
export function formatRelativeFine(value: ApiTimestamp): string {
  const date = toDate(value)
  if (!date) return 'just now'
  const ms = Date.now() - date.getTime()
  const s = Math.max(0, Math.floor(ms / 1000))
  if (s < 60) return 'just now'
  const m = Math.floor(s / 60)
  if (m < 60) return `${m} min ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  return `${d}d ago`
}
